import React, { useState } from 'react';
import { api } from '../../api/client';
import { Alert, Button, Check, Field, Input, Select } from '../../ui/basics';
import { Drawer, useToast } from '../../ui/overlay';
import { rules, useFormValidation } from '../../validation';

// Pages that show team data: a user who can open one of them must have at least one team (matches the server).
const TEAM_PAGES = ['recruitment', 'placements', 'reports', 'analytics'];

const schemaFor = (editing) => ({
  email: [rules.required('Email'), rules.email('Email'), rules.maxLength(120, 'Email')],
  display_name: rules.maxLength(120, 'Display name'),
  ...(editing ? {} : { password: [rules.required('Temporary password'),
    rules.check((v) => String(v).length >= 8, 'Password must be at least 8 characters.'),
    rules.check((v) => !/^\d+$/.test(String(v)), 'Password cannot be only numbers.'),
    rules.check((v, all) => !String(all.email).trim() || String(v).toLowerCase() !== String(all.email).trim().toLowerCase(), 'Password cannot be the same as the email.')] }),
  organizations: rules.check((list) => list.length > 0, 'Pick at least one organization.'),
  pages: rules.check((list) => list.length > 0, 'Pick at least one page.'),
  teams: rules.check((teams, all) => teams.length > 0 || !all.pages.some((p) => TEAM_PAGES.includes(p)), 'Pick at least one team (or All teams) for the pages you selected.'),
});

const toggle = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

export default function UserForm({ user, options, onClose, onSaved }) {
  const toast = useToast();
  const editing = Boolean(user);
  const [original] = useState(() => (user ? { ...user, password: '', display_name: user.display_name || '' } : null));
  const [form, setForm] = useState(() => original || {
    email: '', display_name: '', password: '', role: '', organizations: [], pages: [], teams: [],
    can_edit: true, is_active: true, must_change_password: true,
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const v = useFormValidation(form, schemaFor(editing), original);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const group = (key, list) => { set({ [key]: list }); v.touch(key); if (key === 'pages') v.touch('teams'); };
  const allTeams = form.teams.includes('*');

  const applyRole = (role) => {
    const preset = options.role_presets[role];
    set(preset ? { role, organizations: preset.organizations, pages: preset.pages, teams: preset.teams } : { role });
  };
  const toggleTeam = (key) => {
    v.touch('teams');
    if (key === '*') return set({ teams: allTeams ? [] : ['*'] });
    set({ teams: toggle(form.teams.filter((t) => t !== '*'), key) });
  };

  const save = async () => {
    setError('');
    if (!v.check()) return;
    setBusy(true);
    const body = {
      email: form.email, display_name: form.display_name, role: form.role, organizations: form.organizations,
      pages: form.pages, teams: form.teams, can_edit: form.can_edit, is_active: form.is_active,
    };
    try {
      if (editing) await api.patch(`users/${user.id}/`, body);
      else await api.post('users/', { ...body, password: form.password, must_change_password: form.must_change_password });
      toast(editing ? 'User updated' : 'User created', 'success');
      onSaved();
    } catch (e) { setError(v.fail(e) ? 'The server found a problem with the highlighted fields.' : e.message); setBusy(false); }
  };

  return (
    <Drawer title={editing ? 'Edit user' : 'Add user'} narrow onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={save}>{editing ? 'Save changes' : 'Create user'}</Button></>}>
      <div className="stack">
        {(v.summary || error) && <Alert>{error || v.summary}</Alert>}
        <Field label="Email *" error={v.shown('email')}><Input type="email" autoComplete="off" value={form.email} onChange={(e) => set({ email: e.target.value })} {...v.props('email')} /></Field>
        <Field label="Display name" error={v.shown('display_name')}><Input value={form.display_name} onChange={(e) => set({ display_name: e.target.value })} {...v.props('display_name')} /></Field>
        {!editing && (
          <Field label="Temporary password *" error={v.shown('password')} hint="At least 8 characters, not only numbers. The user must change it at first sign-in.">
            <Input type="text" autoComplete="off" value={form.password} onChange={(e) => set({ password: e.target.value })} {...v.props('password')} />
          </Field>
        )}
        <Field label="Role preset" hint="Choosing a role fills in the access below. You can fine-tune it afterwards.">
          <Select value={form.role} onChange={(e) => applyRole(e.target.value)} options={options.roles} placeholder="Custom access" />
        </Field>
        <Field label="Organizations *" error={v.shown('organizations')}>
          <div className="stack" style={{ gap: 8 }}>{options.organizations.map((o) => (
            <Check key={o.key} label={o.label} checked={form.organizations.includes(o.key)} onChange={() => group('organizations', toggle(form.organizations, o.key))} />))}</div>
        </Field>
        <Field label="Pages *" error={v.shown('pages')}>
          <div className="stack" style={{ gap: 8 }}>{options.pages.map((p) => (
            <Check key={p.key} label={p.label} checked={form.pages.includes(p.key)} onChange={() => group('pages', toggle(form.pages, p.key))} />))}</div>
        </Field>
        <Field label="Teams" error={v.shown('teams')} hint="Which teams' data this user can see in Recruitment, Placements, Reports and Analytics.">
          <div className="stack" style={{ gap: 8 }}>{options.teams.map((t) => (
            <Check key={t.key} label={t.label} checked={allTeams ? true : form.teams.includes(t.key)} disabled={allTeams && t.key !== '*'}
              onChange={() => toggleTeam(t.key)} />))}</div>
        </Field>
        <Check label="Can add and edit data (turn off for view-only access)" checked={form.can_edit} onChange={(e) => set({ can_edit: e.target.checked })} />
        {editing && <Check label="Account is active" checked={form.is_active} onChange={(e) => set({ is_active: e.target.checked })} />}
      </div>
    </Drawer>
  );
}
